import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { BundleInfo, CapacitorUpdater, LatestVersion } from '@capgo/capacitor-updater';

export const OTA_UPDATE_PAGE_PATH = '/ota-update';
/** Query parameters of an optional update: the update page goes back to `from` when it cannot apply the bundle. */
const OTA_OPTIONAL_UPDATE_PARAM = 'optional';
const OTA_RETURN_PATH_PARAM = 'from';
/**
 * Returning to the app after at least this long in the background applies a waiting update. After a short switch
 * (copy a code, answer a message) the user is mid-task, so the update waits for a longer break or the next launch.
 */
export const OTA_RESUME_MIN_BACKGROUND_MS = 5 * 60 * 1000;

const FORCED_UPDATE_ATTEMPT_KEY = 'ota.forcedUpdateAttemptAt';
/** A bundle applied this recently that the backend still refuses means retrying will not help. */
const FORCED_UPDATE_RETRY_WINDOW_MS = 2 * 60 * 1000;

/**
 * - `applying`: the new bundle is set and the app reloads with it.
 * - `no-update`: no newer bundle exists yet for this native build. The update page checks again every 30 s. A store
 *   build that is too old gets `APP_VERSION_TOO_LOW` first (`/update-app`), which the backend checks before this.
 * - `recently-applied`: a bundle was applied in the last minutes and is still refused.
 * - `failed`: the check or the download failed (network, server).
 * - `web`: no plugin (browser); the page reloads the `from` path.
 */
export type OtaForcedUpdateResult = 'applying' | 'no-update' | 'recently-applied' | 'failed' | 'web';

export async function notifyOtaAppReady(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  try {
    await CapacitorUpdater.notifyAppReady();
  } catch (error) {
    console.error('[ota] notifyAppReady failed', error);
  }
}

const readLastForcedUpdateAttempt = (): number => {
  try {
    return Number(localStorage.getItem(FORCED_UPDATE_ATTEMPT_KEY)) || 0;
  } catch {
    return 0;
  }
};

const saveForcedUpdateAttempt = (): void => {
  try {
    localStorage.setItem(FORCED_UPDATE_ATTEMPT_KEY, String(Date.now()));
  } catch {
    // Without storage the retry guard is lost; the update itself still works.
  }
};

/** How long to wait for a download the plugin's own update has already started. */
const PLUGIN_DOWNLOAD_WAIT_MS = 60 * 1000;

const isUsableBundle = (bundle: BundleInfo, version: string): boolean =>
  bundle.version === version && (bundle.status === 'pending' || bundle.status === 'success');

const findBundles = async (version: string): Promise<{ ready?: BundleInfo; downloading: boolean }> => {
  // raw: the stored bundle info, which includes a download in progress; without it only finished bundles are listed
  const { bundles } = await CapacitorUpdater.list({ raw: true });
  return {
    ready: bundles.find((bundle) => isUsableBundle(bundle, version)),
    downloading: bundles.some((bundle) => bundle.version === version && bundle.status === 'downloading'),
  };
};

/** Resolves with the bundle when the plugin finishes downloading `version`, or null when it fails or takes too long. */
const watchPluginDownload = (version: string): { result: Promise<BundleInfo | null>; stop: () => void } => {
  const listeners: Promise<PluginListenerHandle>[] = [];
  let finish: (bundle: BundleInfo | null) => void = () => undefined;

  const result = new Promise<BundleInfo | null>((resolve) => {
    const timer = setTimeout(() => finish(null), PLUGIN_DOWNLOAD_WAIT_MS);
    finish = (bundle) => {
      clearTimeout(timer);
      listeners.forEach((listener) => listener.then((handle) => handle.remove()).catch(() => undefined));
      resolve(bundle);
    };
  });

  listeners.push(
    CapacitorUpdater.addListener('downloadComplete', ({ bundle }) => {
      if (bundle.version === version) finish(bundle);
    }),
    CapacitorUpdater.addListener('downloadFailed', (event) => {
      if (event.version === version) finish(null);
    }),
  );

  return { result, stop: () => finish(null) };
};

/**
 * Returns a bundle of the latest version on the device, without downloading it twice: on a cold start or a return to
 * the foreground, the plugin's own update often has it already, or is still downloading it.
 */
const getLatestBundle = async (latest: LatestVersion & { url: string }): Promise<BundleInfo> => {
  const download = watchPluginDownload(latest.version);
  try {
    const { ready, downloading } = await findBundles(latest.version);
    if (ready) return ready;

    if (downloading) {
      const downloaded = await download.result;
      if (downloaded) return downloaded;
      const { ready: readyAfterWait } = await findBundles(latest.version);
      if (readyAfterWait) return readyAfterWait;
    }
  } finally {
    download.stop();
  }

  return CapacitorUpdater.download({
    url: latest.url,
    version: latest.version,
    sessionKey: latest.sessionKey,
    checksum: latest.checksum,
  });
};

/**
 * Applies the latest OTA bundle of the channel at once (the app reloads). Used when the backend answers
 * `OTA_BUNDLE_VERSION_TOO_LOW`, and for the optional update when the app returns from the background.
 */
export async function applyLatestOtaBundle({ ignoreRecentAttempt = false } = {}): Promise<OtaForcedUpdateResult> {
  if (!Capacitor.isNativePlatform()) return 'web';
  if (!ignoreRecentAttempt && Date.now() - readLastForcedUpdateAttempt() < FORCED_UPDATE_RETRY_WINDOW_MS) {
    return 'recently-applied';
  }

  try {
    // Builds without OTA (dev) have no update URL; iOS getLatest would crash on it.
    const { enabled } = await CapacitorUpdater.isAutoUpdateEnabled();
    if (!enabled) return 'no-update';

    const latest = await CapacitorUpdater.getLatest();
    if (!latest.url || latest.kind === 'up_to_date' || latest.kind === 'blocked') return 'no-update';

    const bundle = await getLatestBundle({ ...latest, url: latest.url });
    saveForcedUpdateAttempt();
    await CapacitorUpdater.set({ id: bundle.id });
    return 'applying';
  } catch (error) {
    console.error('[ota] forced update failed', error);
    return 'failed';
  }
}

/** Whether the channel has a bundle that this app does not run yet. Never throws: a failed check means no update. */
export async function isOtaUpdateAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  try {
    const { enabled } = await CapacitorUpdater.isAutoUpdateEnabled();
    if (!enabled) return false;

    const latest = await CapacitorUpdater.getLatest();
    return Boolean(latest.url) && latest.kind !== 'up_to_date' && latest.kind !== 'blocked';
  } catch (error) {
    console.error('[ota] update check failed', error);
    return false;
  }
}

const RETURN_PATH_PARSE_BASE = 'https://app.invalid';
const RETURN_PATH_KEY = 'ota.returnPath';
/**
 * A saved return path older than this is not from the update that just reloaded the app. Saved before the download,
 * which can take minutes on a slow connection; read-once and the start-at-`/` condition stop a stale entry.
 */
const RETURN_PATH_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * `path` as a page of this app (pathname, query and hash), or `/` when it is not one. Parses against a fixed host
 * instead of comparing prefixes: browsers drop tabs and newlines in a URL (`/\t/host` becomes `//host`) and resolve
 * dot segments (`/.//host` becomes `//host`). The update page itself and any parse error also give `/`.
 */
const toAppPath = (path: string): string => {
  if (!path.startsWith('/') || path.includes('\\')) return '/';

  try {
    const url = new URL(path, RETURN_PATH_PARSE_BASE);
    const isUpdatePage = decodeURIComponent(url.pathname).toLowerCase().startsWith(OTA_UPDATE_PAGE_PATH);
    if (url.host !== new URL(RETURN_PATH_PARSE_BASE).host || url.pathname.startsWith('//') || isUpdatePage) return '/';
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '/';
  }
};

/**
 * The page to go back to when an optional update cannot be applied, or null for a forced update. Only a path of this
 * app is accepted, so the parameter cannot send the user to another site.
 */
export const readOtaReturnPath = (search: string): string | null => {
  const params = new URLSearchParams(search);
  if (params.get(OTA_OPTIONAL_UPDATE_PARAM) !== '1') return null;
  return toAppPath(params.get(OTA_RETURN_PATH_PARAM) ?? '/');
};

/** `from` of the update page as a path of this app, else `/`. Ignores `optional`. */
export const readOtaWebReturnPath = (search: string): string =>
  toAppPath(new URLSearchParams(search).get(OTA_RETURN_PATH_PARAM) ?? '/');

export const OTA_WEB_RELOAD_WINDOW_MS = 2 * 60 * 1000;
/** sessionStorage key (ms epoch): set just before the tab loads a page on its own for a forced update. */
const WEB_RELOAD_AT_KEY = 'ota.webReloadAt';
let webReactionStarted = false;

/** True once the web reaction to a forced update has started in this page load. */
export const isOtaWebReactionStarted = (): boolean => webReactionStarted;

/** Clears the once-per-page-load flag. Tests only: throws outside Vitest (the production build defines `process.env` as `{}`). */
export const resetOtaWebReactionForTests = (): void => {
  if (typeof process === 'undefined' || !process.env.VITEST) {
    throw new Error('resetOtaWebReactionForTests is for tests only');
  }
  webReactionStarted = false;
};

/** Writes `ota.webReloadAt` = now (ignores storage errors), then loads the `from` path of the update page. */
export const loadOtaWebReturnPath = (search: string): void => {
  try {
    sessionStorage.setItem(WEB_RELOAD_AT_KEY, String(Date.now()));
  } catch {
    // Without storage the guard is lost; the page still loads.
  }
  window.location.assign(readOtaWebReturnPath(search));
};

/**
 * Web reaction to `OTA_BUNDLE_VERSION_TOO_LOW`. Acts once per page load: without the flag, several requests refused in
 * the same page load would race, and the 2nd would see the fresh timestamp and open the update page over the pending
 * reload. Reloads once; a refusal within 2 minutes of the last reload opens the update page. Without usable storage
 * the tab never reloads on its own.
 */
export function reactToOtaBundleTooLowOnWeb(): void {
  if (webReactionStarted || window.location.pathname === OTA_UPDATE_PAGE_PATH) return;
  webReactionStarted = true;

  let last: number | null;
  try {
    last = Number(sessionStorage.getItem(WEB_RELOAD_AT_KEY)) || 0;
  } catch {
    last = null;
  }

  if (last !== null && Date.now() - last >= OTA_WEB_RELOAD_WINDOW_MS) {
    let recorded = false;
    try {
      sessionStorage.setItem(WEB_RELOAD_AT_KEY, String(Date.now()));
      recorded = true;
    } catch {
      // Without a recorded time a reload could loop; fall through to the update page.
    }
    if (recorded) {
      window.location.reload();
      return;
    }
  }

  const { pathname, search, hash } = window.location;
  window.location.assign(`${OTA_UPDATE_PAGE_PATH}?${new URLSearchParams({ from: `${pathname}${search}${hash}` })}`);
}

/** Remembers the page to show after the update reloads the app: the plugin reloads it at the start URL (`/`). */
export const saveOtaReturnPath = (path: string): void => {
  try {
    localStorage.setItem(RETURN_PATH_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch {
    // Without storage the user lands on the home page after the update; the update itself still works.
  }
};

export const clearOtaReturnPath = (): void => {
  try {
    localStorage.removeItem(RETURN_PATH_KEY);
  } catch {
    // Nothing to clear without storage.
  }
};

/**
 * Call once at startup, before the router reads the URL. After an optional update, the app starts at `/`; this puts
 * back the page the user was on. The entry is used once, only if it is recent and the app started at `/`.
 */
export const restoreOtaReturnPath = (): void => {
  let saved: { path?: unknown; at?: unknown } | null = null;
  try {
    saved = JSON.parse(localStorage.getItem(RETURN_PATH_KEY) ?? 'null');
  } catch {
    saved = null;
  }
  clearOtaReturnPath();

  if (!saved || typeof saved.path !== 'string' || typeof saved.at !== 'number') return;
  if (Date.now() - saved.at > RETURN_PATH_MAX_AGE_MS || window.location.pathname !== '/') return;

  const path = toAppPath(saved.path);
  if (path !== '/') window.history.replaceState(window.history.state, '', path);
};

const openOptionalOtaUpdate = (): void => {
  const { pathname, search, hash } = window.location;
  const params = new URLSearchParams({
    [OTA_OPTIONAL_UPDATE_PARAM]: '1',
    [OTA_RETURN_PATH_PARAM]: `${pathname}${search}${hash}`,
  });
  window.location.assign(`${OTA_UPDATE_PAGE_PATH}?${params.toString()}`);
};

/**
 * When the app returns from the background after `OTA_RESUME_MIN_BACKGROUND_MS`, checks for a newer OTA bundle and
 * opens the update page if one exists. Without this, the plugin applies it only at the next cold start or after the
 * next time the app goes to the background. Returns a function that removes the listeners.
 */
export function watchOtaUpdatesOnResume(onUpdateAvailable: () => void = openOptionalOtaUpdate): () => void {
  if (!Capacitor.isNativePlatform()) return () => undefined;

  let pausedAt: number | null = null;
  let isChecking = false;

  const listeners = [
    CapacitorApp.addListener('pause', () => {
      pausedAt = Date.now();
    }),
    CapacitorApp.addListener('resume', async () => {
      const backgroundMs = pausedAt === null ? 0 : Date.now() - pausedAt;
      pausedAt = null;
      if (backgroundMs < OTA_RESUME_MIN_BACKGROUND_MS || isChecking) return;
      if (window.location.pathname === OTA_UPDATE_PAGE_PATH) return;

      isChecking = true;
      try {
        if (await isOtaUpdateAvailable()) onUpdateAvailable();
      } finally {
        isChecking = false;
      }
    }),
  ];

  return () => {
    listeners.forEach((listener) => listener.then((handle) => handle.remove()).catch(() => undefined));
  };
}
